import FuseUtils from '@fuse/utils/FuseUtils';
import axios from 'axios';
import {jwtDecode} from 'jwt-decode';

/* eslint-disable camelcase */

class JwtService extends FuseUtils.EventEmitter {
	init() {
		this.setInterceptors();
		this.handleAuthentication();
	}

	setInterceptors = () => {
		axios.interceptors.request.use(config => {
			let apiKey 
			if (config?.url?.startsWith(process.env.REACT_APP_MDM_API_URL)) {
				apiKey = process.env.REACT_APP_AWS_MDM_API_KEY;
			}
			config.headers['x-api-key'] = apiKey;
			const token = localStorage.getItem('jwt_access_token');
			config.headers.Authorization = token ? `Bearer ${token}` : '';
			return config;
		});
		axios.interceptors.response.use(
			response => {
				return response;
			},
			err => {
				return new Promise((resolve, reject) => {
					const CURRENT_USER_URL = `${process.env.REACT_APP_AUTH_URL}/api/${process.env.REACT_APP_AUTH_KEY}/users/current`;
					const LOGIN_URL = `${process.env.REACT_APP_AUTH_URL}/api/${process.env.REACT_APP_AUTH_KEY}/login`;
					if (
						(err.response && err.response.status === 401) ||
						[CURRENT_USER_URL, LOGIN_URL].includes(err.config.url)
					) {
						// if you ever get an unauthorized response, logout the user
						this.emit('onAutoLogout', 'Invalid access_token');
						this.setSession(null);
						// two levels of redirection
						// send me to the auth server and let that redirect to the /api/{productCode/ which will rederict to current page
						const url = new URL(window.location.href);
						url.searchParams.delete('token');
						url.searchParams.delete('key');
						url.searchParams.delete('userId');
						const finalDestination = encodeURIComponent(url.toString());
						const casoAuthTokenGenerator = encodeURIComponent(
							`/api/${process.env.REACT_APP_AUTH_KEY}/redirect?url=${finalDestination}`
						);
						window.location.href = `${process.env.REACT_APP_AUTH_URL}/auth/login?redirect=${casoAuthTokenGenerator}`;
					}
					throw err;
				});
			}
		);
	};

	handleAuthentication = () => {
		const access_token = this.getAccessToken();
		if (!access_token) {
			this.emit('onNoAccessToken');
			return;
		}

		if (this.isAuthTokenValid(access_token)) {
			this.setSession(access_token);
			this.emit('onAutoLogin', true);
		} else {
			this.setSession(null);
			this.emit('onAutoLogout', 'access_token expired');
		}
	};

	createUser = data => {
		return new Promise((resolve, reject) => {
			axios.post('/api/auth/register', data).then(response => {
				if (response.data.user) {
					this.setSession(response.data.access_token);
					resolve(response.data.user);
				} else {
					reject(response.data.error);
				}
			});
		});
	};

	signInWithEmailAndPassword = params => {
		const payload = {
			token: params.token,
			userId: params.userId
		};
		return new Promise((resolve, reject) => {
			axios.post(`${process.env.REACT_APP_AUTH_URL}/api/${params.key}/login`, payload).then(response => {
				if (response.data) {
					this.setSession(response.data.token);
					this.setUserSession(response.data);
					resolve(response.data);
				} else {
					reject(response.data.error);
				}
			});
		});
	};

	getUserData = () => {
		return window.localStorage.getItem('userData');
	};

	signInWithToken = () => {
		return new Promise((resolve, reject) => {
			const userData = this.getUserData();
			const parseUserData = JSON.parse(userData);
			if (parseUserData && parseUserData.roles) {
				const roles = parseUserData.roles.map(data => data.idKey);
				const checkRoleValidation = Array.from(roles).includes('SCHEDU');
				if (checkRoleValidation) {
					resolve(parseUserData);
				} else {
					reject(new Error('Failed to login with token.'));
				}
			} else {
				reject(new Error('Failed to login with token.'));
			}
		});
	};

	updateUserData = user => {
		return axios.post('/api/auth/user/update', {
			user
		});
	};

	setSession = access_token => {
		if (access_token) {
			localStorage.setItem('jwt_access_token', access_token);
			axios.defaults.headers.common.Authorization = `Bearer ${access_token}`;
		}
	};

	setUserSession = userData => {
		if (userData) {
			localStorage.setItem('userData', JSON.stringify(userData));
		}
	};

	logout = () => {
		this.setSession(null);
	};

	isAuthTokenValid = access_token => {
		if (!access_token) {
			return false;
		}
		const decoded = jwtDecode(access_token);
		const currentTime = Date.now() / 1000;
		if (decoded.exp < currentTime) {
			console.warn('access token expired');
			return false;
		}

		return true;
	};

	getAccessToken = () => {
		return window.localStorage.getItem('jwt_access_token');
	};
}

const instance = new JwtService();

export default instance;
